import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { renderClientName, SaveParams, getApptErrorOptions, saveLocalParams, Images, useDiagnosisMatchEnabled } from 'utils';
import {
    DateTypeSelector,
    DownloadCsv,
    SelectTypeAutocomplete,
    SelectAutocompletePagination,
    CheckboxesTags,
    CloseButton,
    ManageActions,
    CustomizedSwitch,
} from 'components';
import { fundingSourceActions, systemActions } from 'store';
import { authEnums, billingStatusList, claimTabEnums } from './constants';
import { Checkbox } from '@mui/material';
import { APPEAL } from '../../../fragments/billing/claims/claimDetails/constants';
import { secondaryBillingList } from '../claimPayment/constants';

export const ClaimFilters = ({}) => {
    const diagnosisMatchEnabled = useDiagnosisMatchEnabled();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const pushInfo = location?.state;
    const fundingSourceList = useSelector((state) => state.fundingSource.fundingSourceList);
    // Offices. Only offered as a filter when the org actually has more than one.
    const companies = useSelector((state) => state.system.companies);
    const multiOffice = companies?.length > 1;
    const memoizedSendParams = useMemo(() => (pushInfo?.payer ? { payer: pushInfo?.payer } : null), [pushInfo?.payer]);

    const isSubmitted = pushInfo?.tabType === claimTabEnums?.SUBMITTED;
    const isBillable = pushInfo?.tabType === claimTabEnums?.BILLABLE || !pushInfo?.tabType;
    const isCompleted = pushInfo?.tabType === claimTabEnums?.COMPLETED;

    // Batch View (Submitted tab only) collapses the filter set to just DOS + Submitted Date.
    const batchView = !!pushInfo?.batchView;

    const activeFiltersCount =
        (!batchView && pushInfo?.payer ? 1 : 0) +
        (!batchView && multiOffice && pushInfo?.company ? 1 : 0) +
        (!batchView && pushInfo?.client ? 1 : 0) +
        (!batchView && isBillable && pushInfo?.withoutAuthId ? 1 : 0) +
        (!batchView && isBillable && pushInfo?.errors?.length ? 1 : 0) +
        (!batchView && isSubmitted && pushInfo?.clearinghouseStatus ? 1 : 0) +
        (!batchView && isSubmitted && pushInfo?.secondaryBillingState ? 1 : 0) +
        (pushInfo?.from || pushInfo?.to ? 1 : 0) +
        (isSubmitted && (pushInfo?.submittedFrom || pushInfo?.submittedTo) ? 1 : 0) +
        (!batchView && isSubmitted && pushInfo?.status === APPEAL ? 1 : 0);

    useEffect(() => {
        dispatch(fundingSourceActions.getFundingSource({ sortType: 'name', orderType: 'ASC' }));
        dispatch(systemActions.getCompanies('noLoad'));
    }, []);

    const handleChange = (e) => {
        const info = {
            ...pushInfo,
        };
        info.skip = 0;
        info.limit = 50;
        info.page = 1;
        // Display-name companions (`funderName`, `clientFull`) ride on the nav state next to
        // the id they describe — the id is what the API filters on, the companion is what the
        // dropdown and the active-filter chip render. Both have to be cleared with their id or
        // the name outlives the filter.
        if (e.target.value === 'All' || !e.target.value) {
            delete info[e.target.name];
            if (e.target.name === 'payer') delete info.funderName;
            if (e.target.name === 'client') delete info.clientFull;
        } else {
            info[e.target.name] = e.target.value;
            if (e.target.name === 'client') {
                info['clientFull'] = e.target?.full;
            }
            if (e.target.name === 'payer') {
                info.funderName = fundingSourceList?.funders?.find((i) => i?.id === e.target.value)?.name;
            }
        }

        const localParams = {};
        info?.payer ? (localParams['payer'] = info?.payer) : delete localParams['payer'];
        // Mirrored from state like every other key — scoping this to `e.target.name === 'payer'`
        // dropped the name from the snapshot whenever any OTHER filter changed, so a restore
        // brought the payer back without its name.
        info?.funderName ? (localParams.funderName = info?.funderName) : delete localParams['funderName'];
        info?.from ? (localParams.from = info?.from) : delete localParams['from'];
        info?.to ? (localParams.to = info?.to) : delete localParams['to'];
        info?.withoutAuthId ? (localParams.withoutAuthId = info?.withoutAuthId) : delete localParams['withoutAuthId'];


        if (e.target.name === 'company') {
            if (e.target.value) {
                localParams.companyName = companies?.find((i) => i.id === info?.company)?.name;
            } else {
                delete localParams['companyName'];
            }
        }

        if (e.target.name === 'payer') {
            if (e.target.value) {
                const founder = fundingSourceList?.funders?.find((i) => i.id === info?.payer);
                localParams.funderName = founder?.name;
            } else {
                delete localParams['funderName'];
            }
        }
        saveLocalParams('claimFilters', localParams);
        SaveParams(navigate, { ...info });
    };

    const handleSelectValue = (list, name) => {
        const params = {
            ...pushInfo,
        };
        params.skip = 0;
        params.limit = 50;
        params.page = 1;
        list?.length ? (params[name] = list) : delete params[name];
        SaveParams(navigate, { ...params });
    };

    const handleResetAllFilters = (handleClose) => {
        const params = {
            ...pushInfo,
        };
        delete params.payer;
        delete params.funderName;
        delete params.company;
        delete params.companyName;
        delete params.client;
        delete params.clientFull;
        delete params.withoutAuthId;
        delete params.errors;
        delete params.clearinghouseStatus;
        delete params.secondaryBillingState;
        delete params.from;
        delete params.to;
        delete params.submittedFrom;
        delete params.submittedTo;
        delete params.status;
        params.skip = 0;
        params.limit = 50;
        params.page = 1;
        saveLocalParams('claimFilters', {});
        SaveParams(navigate, { ...params });

        if (handleClose) {
            handleClose();
        }
    };

    const handleToggleBatchView = () => {
        const params = {
            // ...pushInfo,
        };
        if (batchView) {
            delete params.batchView;
        } else {
            params.batchView = true;
        }
        params.tabType = pushInfo.tabType;
        params.skip = 0;
        params.limit = 50;
        params.page = 1;
        SaveParams(navigate, { ...params });
    };

    const downloadCsvType =
        isBillable ? 'billableAppt' :
            pushInfo?.tabType === claimTabEnums?.PENDING ? 'pendingClaims' :
                isSubmitted ? 'billedClaims' :
                    isCompleted ? 'completedClaims'
              : '';

    return (
        <div className="table-invoice-filter-section">
            {isSubmitted && (
                <div className="batch-view-switcher">
                    <CustomizedSwitch checked={batchView} handleClick={handleToggleBatchView} checkedBgColor={'#347AF0'} />
                    <p>Batch View</p>
                </div>
            )}

            <div className="billing-invoices-filter-box" style={{ alignItems: 'flex-end' }}>
                <ManageActions
                    styleBodyItems={{ padding: 0 }}
                    btnClass={`filter-passive-btn ${activeFiltersCount > 0 ? 'active-filter' : ''}`}
                    renderBtnView={(anchorEl) => (
                        <div>
                            {activeFiltersCount > 0 && <div className="active-filters-count-badge">{activeFiltersCount}</div>}
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <img src={Images.filters} alt="icon" />
                            </div>
                        </div>
                    )}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    wrapperStyles={{ position: 'relative' }}
                    renderBody={(handleClose) => (
                        <div style={{ minWidth: '280px' }}>
                            <div className="schedule-filter-popper-header">
                                <div className="flex-align-center" style={{ gap: 8 }}>
                                    <img src={Images.filters} alt="icon" />
                                    <p>Filters</p>
                                </div>
                                <CloseButton handleCLic={handleClose} />
                            </div>

                            <div className="schedule-filter-popper-body">
                                <div className="filter-popper-body-selectors">
                                    {/* Payer — all tabs (hidden in Batch View) */}
                                    {!batchView && (
                                        <SelectTypeAutocomplete
                                            placeholder={pushInfo?.payer ? '' : 'All'}
                                            name={'payer'}
                                            handleSelect={handleChange}
                                            defaultValue={pushInfo?.payer}
                                            defaultSelected={{ name: pushInfo?.funderName }}
                                            list={
                                                fundingSourceList?.funders
                                                    ? [{ id: 'All', name: 'All' }, ...fundingSourceList?.funders]
                                                    : [{ id: 'All', name: 'All' }]
                                            }
                                            renderValue={(i) => i?.name}
                                            uiType={'tableFilter'}
                                            outLabel={'Payer'}
                                            noError={true}
                                        />
                                    )}
                                    {/* Client — all tabs (hidden in Batch View) */}
                                    {!batchView && (
                                        <SelectAutocompletePagination
                                            outLabel={'Client'}
                                            name={'client'}
                                            handleSelect={handleChange}
                                            placeholder={'All'}
                                            defaultValue={pushInfo?.client}
                                            renderValue={(i) => renderClientName(i)}
                                            type={'client'}
                                            uiType={'tableFilter'}
                                            noError={true}
                                            currentSelected={pushInfo?.clientFull && pushInfo?.clientFull}
                                            sendParams={memoizedSendParams}
                                        />
                                    )}
                                    {/* Auth# — Billable only */}
                                    {isBillable && (
                                        <SelectTypeAutocomplete
                                            placeholder={pushInfo?.withoutAuthId ? '' : 'All'}
                                            name={'withoutAuthId'}
                                            handleSelect={handleChange}
                                            defaultValue={pushInfo?.withoutAuthId}
                                            defaultSelected={{ name: pushInfo?.withoutAuthId }}
                                            list={[
                                                { id: 'All', name: 'All' },
                                                { id: authEnums.WITH_AUTH, name: 'With Auth #' },
                                                { id: authEnums.WITHOUT_AUTH, name: 'Without Auth #' },
                                            ]}
                                            renderValue={(i) => i?.name}
                                            uiType={'tableFilter'}
                                            outLabel={'Auth#'}
                                            noError={true}
                                        />
                                    )}
                                    {/* Errored — Billable only */}
                                    {isBillable && (
                                        <CheckboxesTags
                                            handleChange={(e) => handleSelectValue(e, 'errors')}
                                            name="errors"
                                            renderValue={(i) => i?.name}
                                            value={pushInfo?.errors ? pushInfo?.errors : []}
                                            permissionsList={getApptErrorOptions(diagnosisMatchEnabled)}
                                            outLabel={'Errored'}
                                            placeholder={pushInfo?.errors?.length ? pushInfo?.errors?.map((i) => i?.name).join(', ') : 'All'}
                                            uiType={'tableFilter'}
                                            notShowError={true}
                                            styles={{ width: 'auto' }}
                                        />
                                    )}
                                    {/* Submission Status — Submitted only (hidden in Batch View) */}
                                    {isSubmitted && !batchView && (
                                        <SelectTypeAutocomplete
                                            placeholder={pushInfo?.clearinghouseStatus ? '' : 'All'}
                                            name={'clearinghouseStatus'}
                                            handleSelect={handleChange}
                                            defaultValue={pushInfo?.clearinghouseStatus}
                                            defaultSelected={{ name: pushInfo?.clearinghouseStatus }}
                                            list={billingStatusList}
                                            renderValue={(i) => i?.name}
                                            uiType={'tableFilter'}
                                            outLabel={'Submission Status'}
                                            noError={true}
                                        />
                                    )}
                                    {/* Sec. Billing — Submitted only (hidden in Batch View) */}
                                    {isSubmitted && !batchView && (
                                        <SelectTypeAutocomplete
                                            placeholder={pushInfo?.secondaryBillingState ? '' : 'All'}
                                            name={'secondaryBillingState'}
                                            handleSelect={handleChange}
                                            defaultValue={pushInfo?.secondaryBillingState}
                                            defaultSelected={{ name: pushInfo?.secondaryBillingState }}
                                            list={[{ id: 'All', name: 'All' }, ...secondaryBillingList]}
                                            customOptions={(i) => (
                                                <div className="flex-align-center" style={{ gap: 4 }}>
                                                    {i?.img && <img src={i?.img} alt="icon" />}
                                                    <p style={{ fontSize: 14, whiteSpace: 'nowrap', color: '#172B4D' }}>{i?.name}</p>
                                                </div>
                                            )}
                                            renderValue={(i) => i?.name}
                                            uiType={'tableFilter'}
                                            outLabel={'Sec. Billing'}
                                            noError={true}
                                        />
                                    )}
                                    {/* DOS — all tabs */}
                                    <DateTypeSelector
                                        startName={'from'}
                                        endName={'to'}
                                        outLabel={'DOS'}
                                        storageSave={(value) => {
                                            const localParams = {};
                                            pushInfo?.payer ? (localParams.payer = pushInfo?.payer) : delete localParams['payer'];
                                            pushInfo?.funderName ? (localParams.funderName = pushInfo?.funderName) : delete localParams['funderName'];
                                            value?.from ? (localParams.from = value?.from) : delete localParams['from'];
                                            value?.to ? (localParams.to = value?.to) : delete localParams['to'];
                                            saveLocalParams('claimFilters', localParams);
                                        }}
                                    />
                                    {/* Submitted Date — Submitted only */}
                                    {isSubmitted && (
                                        <DateTypeSelector
                                            startName={'submittedFrom'}
                                            endName={'submittedTo'}
                                            outLabel={'Submitted Date'}
                                            storageSave={(value) => {
                                                const localParams = {};
                                                pushInfo?.payer ? (localParams.payer = pushInfo?.payer) : delete localParams['payer'];
                                                pushInfo?.funderName ? (localParams.funderName = pushInfo?.funderName) : delete localParams['funderName'];
                                                pushInfo?.from ? (localParams.from = pushInfo?.from) : delete localParams['from'];
                                                pushInfo?.to ? (localParams.to = pushInfo?.to) : delete localParams['to'];
                                                value?.submittedFrom ? (localParams.submittedFrom = value?.submittedFrom) : delete localParams['submittedFrom'];
                                                value?.submittedTo ? (localParams.submittedTo = value?.submittedTo) : delete localParams['submittedTo'];
                                                saveLocalParams('claimFilters', localParams);
                                            }}
                                        />
                                    )}
                                    {/* Office — only when the org has more than one (hidden in Batch View) */}
                                    {!batchView && multiOffice && (
                                        <SelectTypeAutocomplete
                                            placeholder={pushInfo?.company ? '' : 'All'}
                                            name={'company'}
                                            handleSelect={handleChange}
                                            defaultValue={pushInfo?.company}
                                            defaultSelected={{ name: pushInfo?.companyName }}
                                            list={companies ? [{ id: 'All', name: 'All' }, ...companies] : [{ id: 'All', name: 'All' }]}
                                            renderValue={(i) => i?.name}
                                            uiType={'tableFilter'}
                                            outLabel={'Office'}
                                            noError={true}
                                        />
                                    )}
                                </div>

                                {/* w/o appeals — Submitted only (hidden in Batch View) */}
                                {isSubmitted && !batchView && (
                                    <div className="schedule-filter-checkboxes-wrapper">
                                        <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                            <Checkbox
                                                onClick={() =>
                                                    handleChange({
                                                        target: {
                                                            value: pushInfo?.status === APPEAL ? 'All' : APPEAL,
                                                            name: 'status',
                                                        },
                                                    })
                                                }
                                                style={{
                                                    padding: 0,
                                                    color: pushInfo?.status === APPEAL ? '#347AF0' : '#D0D5DD',
                                                    borderWidth: 1,
                                                }}
                                                checked={pushInfo?.status === APPEAL}
                                            />
                                            <p style={{ margin: 0 }}>w/o appeals</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="schedule-filter-popper-footer">
                                <button
                                    disabled={activeFiltersCount === 0}
                                    style={{
                                        opacity: activeFiltersCount > 0 ? 1 : 0.5,
                                        pointerEvents: activeFiltersCount > 0 ? 'auto' : 'none',
                                    }}
                                    onClick={() => handleResetAllFilters(handleClose)}
                                >
                                    <img src={Images.revertWhite} alt="reset" />
                                    <p>Reset all</p>
                                </button>

                                <p className="schedule-filter-footer-result">
                                    {activeFiltersCount} result{activeFiltersCount !== 1 ? 's' : ''}
                                </p>
                            </div>
                        </div>
                    )}
                />
            </div>

            <div className="billing-invoices-filter-box">
                <DownloadCsv fromModal={true} type={downloadCsvType} />
            </div>
        </div>
    );
};
